/**
 * Enhanced Authentication Hook - React Integration
 *
 * Provides comprehensive authentication state management for React components.
 * Integrates Better Auth with additional utilities and type safety.
 *
 * @example
 * ```typescript
 * function LoginPage() {
 *   const { user, isLoading, signIn, signOut, isAuthenticated } = useAuth();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (isAuthenticated) return <Dashboard user={user} />;
 *   return <LoginForm onLogin={signIn} />;
 * }
 * ```
 */


import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  signIn as authSignIn,
  signUp as authSignUp,
  signOut as authSignOut,
  useSession,
  sessionUtils,
  type AuthError
} from '@/lib/auth-client';
import { usePostHog } from 'posthog-js/react';

/**
 * Enhanced Authentication Hook
 *
 * Combines Better Auth session management with additional utilities
 * for comprehensive authentication state management.
 */
export function useAuth() {
  const navigate = useNavigate();
  const session = useSession();
  const posthog = usePostHog();

  // Additional state management
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);

  // Derived state from Better Auth session
  const isLoading = session.isPending;
  const isAuthenticated = !!session.data?.user;
  const user = session.data?.user || null;
  const sessionData = session.data?.session || null;

  /**
   * Check admin status when user changes
   * Only re-run when user ID changes (optimization: avoid re-check on user object mutation)
   */
  const userId = user?.id;
  useEffect(() => {
    const checkAdmin = async () => {
      if (userId) {
        const adminStatus = await sessionUtils.isAdmin();
        setIsAdmin(adminStatus);
      } else {
        setIsAdmin(false);
      }
    };

    checkAdmin();
  }, [userId]);

  /**
   * Clear error when session changes
   */
  useEffect(() => {
    setError(null);
  }, [session.data]);

  /**
   * Enhanced Sign In with Error Handling
   */
  const signIn = {
    email: useCallback(async (credentials: { email: string; password: string; rememberMe?: boolean }) => {
      try {
        setError(null);
        const result = await authSignIn.email({
          email: credentials.email,
          password: credentials.password,
          rememberMe: credentials.rememberMe || false
        });

        if (result.error) {
          if (result.error.code === 'EMAIL_NOT_VERIFIED') {
            throw new Error('Confirm your email first. We sent a new confirmation link to your inbox.');
          }
          setError({
            message: result.error.message || 'Login failed',
            status: result.error.status || 400,
            statusText: result.error.statusText || 'Bad Request'
          });
          throw new Error(result.error.message || 'Login failed');
        }

        // Successful login - redirect will be handled by the calling component
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
        setError({
          message: errorMessage,
          status: 500,
          statusText: 'Internal Server Error'
        });
        throw err;
      }
    }, [])
  };

  /**
   * Enhanced Sign Up with Error Handling
   */
  const signUp = {
    email: useCallback(async (data: { email: string; password: string; name: string; callbackURL?: string }) => {
      try {
        setError(null);
        const result = await authSignUp.email({
          email: data.email,
          password: data.password,
          name: data.name,
          // Where the confirmation link lands once the email is verified
          ...(data.callbackURL ? { callbackURL: data.callbackURL } : {}),
        });

        if (result.error) {
          setError({
            message: result.error.message || 'Registration failed',
            status: result.error.status || 400,
            statusText: result.error.statusText || 'Bad Request'
          });
          throw new Error(result.error.message || 'Registration failed');
        }

        // Successful registration - redirect will be handled by the calling component
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
        setError({
          message: errorMessage,
          status: 500,
          statusText: 'Internal Server Error'
        });
        throw err;
      }
    }, [])
  };

  /**
   * Enhanced Sign Out with Navigation
   */
  const signOut = useCallback(async () => {
    try {
      setError(null);
      posthog.capture('user_logged_out')
      posthog.reset()
      await authSignOut();
      setIsAdmin(false);
      navigate({ to: '/login' });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign out failed';
      setError({
        message: errorMessage,
        status: 500,
        statusText: 'Internal Server Error'
      });
    }
  }, [navigate, posthog]);

  /**
   * Legacy compatibility - handleLogout alias
   */
  const handleLogout = signOut;

  /**
   * Refresh Session Data
   * Only re-run when user ID changes (optimization: avoid callback recreation)
   */
  const refreshSession = useCallback(async () => {
    try {
      setError(null);
      // Better Auth useSession hook automatically refreshes
      // We can trigger a re-check of admin status
      if (userId) {
        const adminStatus = await sessionUtils.isAdmin();
        setIsAdmin(adminStatus);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Session refresh failed';
      setError({
        message: errorMessage,
        status: 500,
        statusText: 'Internal Server Error'
      });
    }
  }, [userId]);

  /**
   * Check Admin Status
   */
  const checkAdminStatus = useCallback(async () => {
    try {
      const adminStatus = await sessionUtils.isAdmin();
      setIsAdmin(adminStatus);
      return adminStatus;
    } catch {
      setIsAdmin(false);
      return false;
    }
  }, []);

  /**
   * Navigation Utilities
   */
  const redirectToLogin = useCallback(() => {
    navigate({ to: '/login' });
  }, [navigate]);

  const redirectToWorkspace = useCallback(() => {
    navigate({ to: '/workspace' });
  }, [navigate]);

  return {
    // Legacy API compatibility
    user,
    isLoading,
    isAuthenticated,
    logout: handleLogout,
    refetch: refreshSession,
    error: session.error || error,

    // Enhanced API
    session: sessionData,
    isAdmin,
    signIn,
    signUp,
    signOut,
    refreshSession,
    checkAdminStatus,
    redirectToLogin,
    redirectToWorkspace
  };
}

/**
 * Authentication State Hook
 *
 * Lighter version that only provides session state without actions.
 * Useful for components that only need to read authentication state.
 */
export function useAuthState() {
  const { user, session, isLoading, isAuthenticated, isAdmin, error } = useAuth();

  return {
    user,
    session,
    isLoading,
    isAuthenticated,
    isAdmin,
    error
  };
}

/**
 * Admin Authentication Hook
 *
 * Specialized hook for admin-only components.
 * Automatically redirects non-admin users.
 */
export function useAdminAuth() {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated && !auth.isAdmin) {
      // User is authenticated but not admin - redirect to workspace
      navigate({ to: '/workspace' });
    } else if (!auth.isLoading && !auth.isAuthenticated) {
      // User is not authenticated - redirect to login
      navigate({ to: '/login' });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.isAdmin, navigate]);

  return auth;
}

/**
 * Protected Route Hook
 *
 * Hook for protecting routes that require authentication.
 * Automatically redirects unauthenticated users to login.
 */
export function useProtectedRoute() {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      navigate({ to: '/login' });
    }
  }, [auth.isLoading, auth.isAuthenticated, navigate]);

  return auth;
}